import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Field } from "@/components/admin/ui";
import { PackageEditor } from "@/components/admin/PackageEditor";
import { api, formatApiError } from "@/lib/api";

const newPackage = () => ({
  id: Math.random().toString(16).slice(2, 10), name: "", duration_days: 1, duration_label: "", mode: "pax",
  price_per_person: 0, price_tiers: [{ pax: 1, price: 0 }], extra_pax_price: 0, min_participants: 1,
  max_participants: 10, includes: "", excludes: "", description: "",
});

const toForm = (t) =>
  t
    ? {
        ...t,
        itinerary: t.itinerary.join("\n"),
        packages: t.packages.map((p) => ({
          ...p, mode: p.price_tiers.length ? "tier" : "pax",
          price_tiers: p.price_tiers.length ? p.price_tiers : [{ pax: 1, price: 0 }],
          includes: p.includes.join("\n"), excludes: p.excludes.join("\n"),
        })),
      }
    : { name: "", mountain: "", location: "", elevation: 0, meeting_point: "", difficulty: "", description: "", itinerary: "", image_url: "", active: true, packages: [newPackage()] };

const lines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);
const num = (v, d = 0) => (v === "" || v == null ? d : Number(v));

const toPayload = (f) => ({
  name: f.name, mountain: f.mountain, location: f.location, elevation: num(f.elevation), meeting_point: f.meeting_point,
  difficulty: f.difficulty, description: f.description, itinerary: lines(f.itinerary), image_url: f.image_url, active: f.active,
  packages: f.packages.map((p) => ({
    id: p.id, name: p.name, duration_days: num(p.duration_days, 1), duration_label: p.duration_label, description: p.description,
    price_per_person: p.mode === "pax" ? num(p.price_per_person) : 0,
    price_tiers: p.mode === "tier" ? p.price_tiers.map((t) => ({ pax: num(t.pax), price: num(t.price) })).filter((t) => t.pax > 0) : [],
    extra_pax_price: p.mode === "tier" ? num(p.extra_pax_price) : 0,
    min_participants: num(p.min_participants, 1), max_participants: num(p.max_participants, 1),
    includes: lines(p.includes), excludes: lines(p.excludes),
  })),
});

export const TripDialog = ({ trip, onClose, onSaved }) => {
  const isEdit = !!trip?.id;
  const [form, setForm] = useState(() => toForm(trip));
  const ev = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setPkgs = (fn) => setForm((f) => ({ ...f, packages: fn(f.packages) }));
  const save = useMutation({
    mutationFn: (payload) => (isEdit ? api.put(`/admin/trips/${trip.id}`, payload) : api.post("/admin/trips", payload)),
    onSuccess: () => { toast.success(isEdit ? "Trip diperbarui" : "Trip ditambahkan"); onSaved(); onClose(); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const submit = (e) => { e.preventDefault(); save.mutate(toPayload(form)); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="scroll-thin max-h-[92vh] max-w-3xl overflow-y-auto border-white/10 bg-ink-900" data-testid="trip-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? "Edit trip" : "Tambah trip"}</DialogTitle>
          <DialogDescription>Atur lokasi, foto, paket, dan harga yang tampil di website.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-8">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Nama trip / gunung"><Input required minLength={2} value={form.name} onChange={ev("name")} data-testid="trip-form-name" /></Field>
            <Field label="Judul tampilan (opsional)"><Input value={form.mountain} onChange={ev("mountain")} placeholder="Kosong = pakai nama trip" data-testid="trip-form-mountain" /></Field>
            <Field label="Lokasi"><Input value={form.location} onChange={ev("location")} placeholder="cth. Gowa, Sulawesi Selatan" data-testid="trip-form-location" /></Field>
            <Field label="Ketinggian (mdpl)" hint="0 = tidak ditampilkan"><Input type="number" min={0} value={form.elevation} onChange={ev("elevation")} data-testid="trip-form-elevation" /></Field>
            <Field label="Titik kumpul"><Input value={form.meeting_point} onChange={ev("meeting_point")} data-testid="trip-form-meeting-point" /></Field>
            <Field label="Tingkat kesulitan"><Input value={form.difficulty} onChange={ev("difficulty")} placeholder="cth. Menengah" data-testid="trip-form-difficulty" /></Field>
            <Field label="URL foto" className="sm:col-span-2">
              <div className="flex items-center gap-3">
                <Input value={form.image_url} onChange={ev("image_url")} placeholder="https://... atau /images/..." data-testid="trip-form-image" />
                {form.image_url && <img src={form.image_url} alt="" className="h-10 w-14 shrink-0 rounded-md object-cover" />}
              </div>
            </Field>
            <Field label="Deskripsi" className="sm:col-span-2"><Textarea rows={3} value={form.description} onChange={ev("description")} data-testid="trip-form-description" /></Field>
            <Field label="Rencana perjalanan (1 per baris, opsional)" className="sm:col-span-2"><Textarea rows={3} value={form.itinerary} onChange={ev("itinerary")} data-testid="trip-form-itinerary" /></Field>
            <label className="flex items-center gap-3 text-sm sm:col-span-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="trip-form-active" />
              Tampilkan di website
            </label>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="font-display text-xl">Paket & harga</p>
              <Button type="button" size="sm" variant="outline" onClick={() => setPkgs((ps) => [...ps, newPackage()])} data-testid="trip-form-add-package"><Plus /> Tambah paket</Button>
            </div>
            {form.packages.map((p, i) => (
              <PackageEditor key={p.id} pkg={p} index={i} canRemove={form.packages.length > 1}
                onChange={(pkg) => setPkgs((ps) => ps.map((x, j) => (j === i ? pkg : x)))}
                onRemove={() => setPkgs((ps) => ps.filter((_, j) => j !== i))} />
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="ghost" onClick={onClose} data-testid="trip-form-cancel">Batal</Button>
            <Button type="submit" disabled={save.isPending} data-testid="trip-form-submit">{save.isPending && <Loader2 className="animate-spin" />} Simpan</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
